// Client for api/functions/catalogue-admin.js — the data layer behind /admin/catalogue.
import { functionsUrl } from './apiBase';
import { getAuthToken } from './api';
import { classifyTags } from '../data/catalogue/tagTaxonomy';

export interface StyleRow {
  kind: 'style';
  /** Loaded on first expand; the table renders these as sub-rows. */
  variants?: VariantRow[];
  style_code: string;
  style_name: string;
  brand: string;
  product_type: string;
  gender: string | null;
  age_group: string | null;
  sustainable_organic: string | null;
  size_range: string | null;
  fabric: string | null;
  categorisation: string | null;
  /** Every feed tag, cleaned, in feed order. */
  feed_tags: string[];
  /** Our pruned view of the tags, see src/data/catalogue/tagTaxonomy.ts. */
  category: string[];
  sector: string[];
  attributes: string[];
  country_of_origin: string | null;
  image_url: string | null;
  swatches: string[];
  is_live: boolean;
  variant_count: number;
  live_count: number;
  colour_count: number;
  size_count: number;
  cost_min: number | null;
  cost_max: number | null;
  updated_at: string;
}

export interface Variant {
  sku_code: string;
  colour_code: string | null;
  colour_name: string | null;
  primary_colour: string | null;
  colour_shade: string | null;
  rgb: string | null;
  colour_image: string | null;
  size_code: string | null;
  size_name: string | null;
  single_price: number | null;
  sku_status: 'Live' | 'Discontinued';
  ean_code: string | null;
  pack_quantity: number | null;
  carton_quantity: number | null;
  primary_product_image_url: string | null;
  updated_at: string;
}

export interface VariantRow extends Variant {
  kind: 'variant';
  style_code: string;
}

export type CatalogueRow = StyleRow | VariantRow;

export interface StyleDetail {
  style_code: string;
  style_name: string;
  brand: string;
  product_type: string;
  gender: string | null;
  age_group: string | null;
  sustainable_organic: string | null;
  size_range: string | null;
  sizing_to_fit: string | null;
  size_exclusions: string | null;
  retail_description: string | null;
  specification: string | null;
  product_feature_1: string | null;
  product_feature_2: string | null;
  product_feature_3: string | null;
  fabric: string | null;
  weight_gsm: string | null;
  washing_instructions: string | null;
  categorisation: string | null;
  accreditations: string | null;
  tag: string | null;
  plus_sizes: string | null;
  country_of_origin: string | null;
  primary_product_image_url: string | null;
  size_guide: string | null;
}

export interface StatusPatch {
  skuCodes?: string[];
  styleCodes?: string[];
  skuStatus: 'Live' | 'Discontinued';
}

export class CatalogueApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Ralawise "Categorisation" is a pipe-separated tag list, cut off by the
// feed at 400 characters, so a full-length string ends in a partial tag.
const splitCategories = (raw: unknown): string[] => {
  if (typeof raw !== 'string' || !raw) return [];
  const parts = raw.split('|');
  if (raw.length >= 380) parts.pop();
  const seen = new Set<string>();
  parts.forEach((t) => {
    const v = t.replace(/�/g, '').trim();
    if (v) seen.add(v);
  });
  return Array.from(seen);
};

// pg returns numeric columns as strings; normalise once at the boundary.
export const normaliseStyle = (r: any): StyleRow => {
  const feed_tags = splitCategories(r.categorisation);
  const c = classifyTags(feed_tags);
  return {
    ...r,
    kind: 'style',
    feed_tags,
    // Styles the tags do not place still get their feed product type.
    category: c.category.length ? c.category : r.product_type ? [String(r.product_type)] : [],
    sector: c.sector,
    attributes: c.attributes,
  variant_count: Number(r.variant_count) || 0,
  live_count: Number(r.live_count) || 0,
  colour_count: Number(r.colour_count) || 0,
  size_count: Number(r.size_count) || 0,
  cost_min: toNum(r.cost_min),
  cost_max: toNum(r.cost_max),
    swatches: Array.isArray(r.swatches) ? r.swatches.filter(Boolean) : [],
    is_live: !!r.is_live,
  };
};

export const normaliseVariant = (v: any): Variant => ({
  ...v,
  single_price: toNum(v.single_price),
  pack_quantity: toNum(v.pack_quantity),
  carton_quantity: toNum(v.carton_quantity),
});

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const res = await fetch(`${functionsUrl('catalogue-admin')}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? ''}`,
      ...(init.headers || {}),
    },
  });
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body */
  }
  if (!res.ok || !body?.success) {
    throw new CatalogueApiError(res.status, body?.error || `Request failed (${res.status})`);
  }
  return body as T;
}

// The style list is ~4k rows and the same for every admin page view, so one
// in-flight/recent result is shared: AdminShell prefetches it on load, and
// opening the Catalogue reuses it. `force` (the refresh button) skips it.
const STYLES_TTL_MS = 5 * 60 * 1000;
let stylesCache: { at: number; promise: Promise<StyleRow[]> } | null = null;

export function fetchStyles({ force = false } = {}): Promise<StyleRow[]> {
  if (!force && stylesCache && Date.now() - stylesCache.at < STYLES_TTL_MS) return stylesCache.promise;
  const promise = request<{ styles: any[] }>('/styles').then((body) => body.styles.map(normaliseStyle));
  stylesCache = { at: Date.now(), promise };
  // A failed load must not be served from the cache.
  promise.catch(() => {
    if (stylesCache?.promise === promise) stylesCache = null;
  });
  return promise;
}

/** Warm the style list in the background; errors are left for the page to report. */
export function prefetchStyles() {
  fetchStyles().catch(() => undefined);
}

/** Drop the cached list after an edit changes it. */
export function invalidateStyles() {
  stylesCache = null;
}

export async function fetchStyle(styleCode: string): Promise<{ style: StyleDetail; variants: Variant[] }> {
  const body = await request<{ style: StyleDetail; variants: any[] }>(`/styles/${encodeURIComponent(styleCode)}`);
  return { style: body.style, variants: body.variants.map(normaliseVariant) };
}

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', '6XL', '7XL', '8XL'];
const sizeKey = (v: Variant): [number, number, string] => {
  const s = (v.size_name ?? v.size_code ?? '').trim().toUpperCase();
  const i = SIZE_ORDER.indexOf(s);
  if (i >= 0) return [0, i, s];
  const n = parseFloat(s.replace(/[^\d.]/g, ''));
  if (Number.isFinite(n)) return [1, n, s];
  return [2, 0, s];
};

// Variants as table sub-rows: colour A→Z, then sizes in wearable order.
export async function fetchVariantRows(styleCode: string): Promise<VariantRow[]> {
  const { variants } = await fetchStyle(styleCode);
  const rows: VariantRow[] = variants.map((v) => ({ ...v, kind: 'variant', style_code: styleCode }));
  rows.sort((a, b) => {
    const c = (a.colour_name ?? '').localeCompare(b.colour_name ?? '');
    if (c) return c;
    const [ka, na, sa] = sizeKey(a);
    const [kb, nb, sb] = sizeKey(b);
    return ka - kb || na - nb || sa.localeCompare(sb);
  });
  return rows;
}

export async function patchStatus(patch: StatusPatch): Promise<{ updated: number; styles: StyleRow[] }> {
  const body = await request<{ updated: number; styles: any[] }>('/variants', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  invalidateStyles();
  return { updated: body.updated, styles: body.styles.map(normaliseStyle) };
}
